// Drag&drop zone Drive-style — upload instantáneo, sin formulario.
// El título se autoextrae del nombre del archivo (sin extensión).
// Único control: toggle "Personal" ↔ "Compartido con la org".
// Después de subir, el usuario puede abrir el ShareDialog desde la lista.

import { useCallback, useRef, useState } from 'react';
import { CheckCircle2, UploadCloud, FileText, Loader2, X } from 'lucide-react';

import { uploadDocument } from '../api';
import type { Visibility } from '../types';

interface Props {
  onUploaded?: () => void;
}

interface UploadItem {
  id: string;        // local nonce
  file: File;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim() || name;
}

export function DocumentUpload({ onUploaded }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [shareWithOrg, setShareWithOrg] = useState(false);
  const [items, setItems] = useState<UploadItem[]>([]);

  const visibility: Visibility = shareWithOrg ? 'org' : 'personal';

  const startUpload = useCallback(async (file: File, visibilityNow: Visibility) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setItems((prev) => [{ id, file, status: 'uploading' }, ...prev]);
    try {
      await uploadDocument({
        file,
        title: titleFromFilename(file.name),
        description: '',
        tags: [],
        visibility: visibilityNow,
      });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: 'done' } : it)));
      onUploaded?.();
    } catch (err: any) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === id ? { ...it, status: 'error', error: err?.message ?? String(err) } : it,
        ),
      );
    }
  }, [onUploaded]);

  const handleFiles = useCallback((files: FileList | File[]) => {
    Array.from(files).forEach((f) => {
      void startUpload(f, visibility);
    });
  }, [startUpload, visibility]);

  return (
    <div className="max-w-3xl space-y-4">
      {/* Toggle de visibilidad — único control antes del drop */}
      <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3">
        <div>
          <div className="text-sm font-medium text-foreground">
            {shareWithOrg ? 'Compartido con la organización' : 'Solo para mí'}
          </div>
          <div className="text-xs text-muted-foreground">
            {shareWithOrg
              ? 'Todos los miembros de tu org podrán ver lo que subas.'
              : 'Privado. Después puedes compartir desde la lista.'}
          </div>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input
            type="checkbox"
            checked={shareWithOrg}
            onChange={(e) => setShareWithOrg(e.target.checked)}
            className="peer sr-only"
          />
          <div className="peer h-6 w-11 rounded-full bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-border after:bg-background after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-primary-foreground" />
        </label>
      </div>

      {/* Drop zone — gigante, sin más controles */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-16 text-center transition ${
          dragOver
            ? 'border-primary bg-primary/10'
            : 'border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg,.webp"
          onChange={(e) => {
            if (e.target.files?.length) handleFiles(e.target.files);
            if (fileRef.current) fileRef.current.value = '';
          }}
          className="hidden"
        />
        <UploadCloud size={48} className="mx-auto mb-3 text-muted-foreground" />
        <p className="text-lg font-medium text-foreground">
          Arrastra archivos aquí o haz click
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Se sube apenas sueltes — sin formulario, sin esperas.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          PDF · Word · TXT · CSV · imágenes — múltiples a la vez · max 20 MB c/u
        </p>
      </div>

      {/* Lista de uploads en progreso/recientes */}
      {items.length > 0 && (
        <div className="space-y-2 rounded-lg border border-border bg-card p-3">
          <div className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Subidas recientes
          </div>
          {items.map((it) => (
            <div
              key={it.id}
              className="flex items-center gap-3 rounded border border-border bg-background px-3 py-2"
            >
              <FileText size={18} className="shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {titleFromFilename(it.file.name)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {(it.file.size / 1024).toFixed(1)} KB · {it.file.name}
                </div>
                {it.status === 'error' && it.error && (
                  <div className="mt-1 text-xs text-destructive">{it.error}</div>
                )}
              </div>
              {it.status === 'uploading' && (
                <Loader2 size={18} className="shrink-0 animate-spin text-primary" />
              )}
              {it.status === 'done' && (
                <CheckCircle2 size={18} className="shrink-0 text-emerald-500" />
              )}
              {it.status === 'error' && (
                <button
                  type="button"
                  onClick={() => setItems((prev) => prev.filter((p) => p.id !== it.id))}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Quitar de la lista"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
